import { useMemo } from 'react';
import { GetSocialAnalyticsPostTypeTimeSeriesQuery } from '@frontend/gql/social/graphql';
import { POST_TYPE_MULTIPLIER_MAP } from './useHistoricalData';
import { POST_TYPE_AWARENESS_MULTIPLIER_MAP } from '../utils/awarenessMultipliers';

type Metric = {
  value: number;
  dollarMultiplier: number;
  dollarValue: number;
};

type SocialMetrics = {
  likes?: Metric;
  comments?: Metric;
  views?: Metric;
  reach?: Metric;
  impressions?: Metric;
  shares?: Metric;
  favorites?: Metric;
  clicks?: Metric;
  closeups?: Metric;
};

type SocialData = {
  instagramPost?: SocialMetrics;
  instagramReel?: SocialMetrics;
  instagramStory?: SocialMetrics;
  instagramVideo?: SocialMetrics;
  tiktok?: SocialMetrics;
  youtube?: SocialMetrics;
  youtubeShort?: SocialMetrics;
  pinterest?: SocialMetrics;
  twitter?: SocialMetrics;
};

const postTypes = [
  { key: 'instagramPost', type: 'instagram_post' },
  { key: 'instagramReel', type: 'instagram_reel' },
  { key: 'instagramStory', type: 'instagram_story' },
  { key: 'instagramVideo', type: 'instagram_video' },
  { key: 'tiktok', type: 'tiktok_video' },
  { key: 'youtube', type: 'youtube_video' },
  { key: 'youtubeShort', type: 'youtube_short' },
  { key: 'pinterest', type: 'pinterest' },
  { key: 'twitter', type: 'twitter' },
];

export const useSocialData = (data: GetSocialAnalyticsPostTypeTimeSeriesQuery): SocialData => {
  return useMemo(() => {
    if (!data?.client?.socialAnalytics?.postTypesTimeSeries?.total) return {};

    return postTypes.reduce((acc, { key, type }) => {
      const postData = data.client.socialAnalytics.postTypesTimeSeries.total.find(
        (item) => item.postType === type
      );

      if (!postData) return acc;

      const dollardollarMultipliers = POST_TYPE_MULTIPLIER_MAP[type] ?? {};
      const awarenessdollarMultipliers = POST_TYPE_AWARENESS_MULTIPLIER_MAP[type] ?? {};

      const metrics: SocialMetrics = {};

      // Collect all metrics keys from both dollarMultiplier sets
      const allMetrics = new Set([
        ...Object.keys(dollardollarMultipliers),
        ...Object.keys(awarenessdollarMultipliers),
      ]);

      (Array.from(allMetrics) as (keyof SocialMetrics)[]).forEach((metric) => {
        const rawValue = postData[metric];
        const dollarMultiplier =
          dollardollarMultipliers[metric] ??
          awarenessdollarMultipliers[metric];

        if (typeof rawValue === 'number' && typeof dollarMultiplier === 'number') {
          metrics[metric] = {
            value: rawValue,
            dollarMultiplier,
            dollarValue: rawValue * dollarMultiplier,
          };
        } else {
          metrics[metric] = {
            value: 0,
            dollarMultiplier: 0,
            dollarValue: 0,
          };
        }
      });

      acc[key] = metrics;
      return acc;
    }, {} as SocialData);
  }, [data]);
};
